import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ToastNotification } from '../../models/toast-notification';
import { ToastNotificationType } from '../../models/enums';
import { TOAST_ANIMATION_TIMEOUT, TOAST_NOTIFICATION_TIMEOUT } from '../../models/constants';

@Injectable({
  providedIn: 'root'
})
export class ToastNotificationService {
  private toastsSubject = new BehaviorSubject<ToastNotification[]>([]);
  public toasts = this.toastsSubject.asObservable();

  // Mapped object timers
  private toastTimers = new Map<string, any>();
  private fadeTimers = new Map<string, any>();

  showToast(message: string, type: ToastNotification['type'] = ToastNotificationType.information, duration: number = TOAST_NOTIFICATION_TIMEOUT) {
    const toast: ToastNotification = new ToastNotification({
      id: this.generateId(),
      message,
      type,
      duration,
      isSlidingInFromRight: true,
      isFadingOut: false
    });

    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([toast, ...currentToasts]);

    setTimeout(() => {
      this.startFadeOut(toast.id);
    }, duration);
  }

  private startTimer(id: string, duration: number) {
    // Clear any existing timer
    this.clearMappedObjectTimer(this.toastTimers, id);

    const timeoutId = setTimeout(() => {
      this.startFadeOut(id);
    }, duration);

    this.toastTimers.set(id, timeoutId);
  }

  pauseTimer(id: string) {
    this.clearMappedObjectTimer(this.toastTimers, id);
    this.cancelFadeOut(id);
  }

  resetTimer(id: string, duration: number = TOAST_NOTIFICATION_TIMEOUT) {
    this.pauseTimer(id);
    this.startTimer(id, duration);
  }

  startFadeOut(id: string) {
    const currentToasts = this.toastsSubject.value;
    const toastIndex = currentToasts.findIndex(toast => toast.id === id);

    if (toastIndex !== -1) {
      const updatedToasts = [...currentToasts];
      updatedToasts[toastIndex] = new ToastNotification({ ...updatedToasts[toastIndex], isSlidingInFromRight: false, isFadingOut: true });
      this.toastsSubject.next(updatedToasts);

      const fadeTimeoutId = setTimeout(() => {
        this.removeToast(id);
      }, TOAST_ANIMATION_TIMEOUT);

      this.fadeTimers.set(id, fadeTimeoutId);
    }
  }

  private cancelFadeOut(id: string) {
    this.clearMappedObjectTimer(this.fadeTimers, id);

    // Reset the fade-out state
    const currentToasts = this.toastsSubject.value;
    const toastIndex = currentToasts.findIndex(toast => toast.id === id);

    if (toastIndex !== -1 && currentToasts[toastIndex].isFadingOut) {
      const updatedToasts = [...currentToasts];
      updatedToasts[toastIndex] = new ToastNotification({ ...updatedToasts[toastIndex], isFadingOut: false });
      this.toastsSubject.next(updatedToasts);
    }
  }

  removeToast(id: string) {
    const currentToasts = this.toastsSubject.value;
    const filteredToasts = currentToasts.filter(toast => toast.id !== id);
    this.toastsSubject.next(filteredToasts);

    // Clean up all timers
    this.clearMappedObjectTimer(this.toastTimers, id);
    this.clearMappedObjectTimer(this.fadeTimers, id);
  }

  success(message: string, duration?: number) {
    this.showToast(message, ToastNotificationType.success, duration);
  }

  error(message: string, duration?: number) {
    this.showToast(message, ToastNotificationType.error, duration);
  }

  warning(message: string, duration?: number) {
    this.showToast(message, ToastNotificationType.warning, duration);
  }

  information(message: string, duration?: number) {
    this.showToast(message, ToastNotificationType.information, duration);
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 9);
  }

  private clearMappedObjectTimer(objectTimers: Map<string, any>, id: string) {
    const timeoutId = objectTimers.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      objectTimers.delete(id);
    }
  }
}